import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { useQuery } from "@apollo/client";
import { GET_USER } from "@/integrations/apollo/operations";
import { JwtUser, JwtSession } from "@/types/user";

export const useAuth = () => {
  const [jwtSession, setJwtSession] = useState<JwtSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();

  // User is authenticated if JWT session exists
  const isAuthenticated = !!jwtSession;

  // If we have a user ID, we'll fetch user data from the API
  const userId = jwtSession?.user?.id;

  // Skip the query if we don't have a user ID
  const { data: userData } = useQuery(GET_USER, {
    variables: { id: userId },
    skip: !userId, // Skip this query when no userId is available
  });

  useEffect(() => {
    // Check for JWT auth from localStorage
    const storedUser = localStorage.getItem("user");

    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setJwtSession(userData);

        // Check admin status for JWT users
        checkJwtAdminStatus(userData.user);
      } catch (err) {
        console.error("Error parsing JWT user data:", err);
        localStorage.removeItem("user"); // Clear invalid data
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, []);

  // Effect to update user data when API response changes
  useEffect(() => {
    if (userData?.user && jwtSession) {
      // Update the user data with the latest from the API
      const updatedSession = {
        ...jwtSession,
        user: {
          ...jwtSession.user,
          ...userData.user,
        },
      };
      setJwtSession(updatedSession);
    }
  }, [userData]);

  // Check admin status for JWT users
  const checkJwtAdminStatus = async (user: JwtUser) => {
    try {
      // Check if user has role property and if it equals 'admin'
      setIsAdmin(user.role === 'admin');
    } catch (err) {
      console.error("Error checking JWT admin status:", err);
      setIsAdmin(false);
    } finally {
      setIsLoading(false);
    }
  };

  const redirectToLogin = () => {
    navigate("/login");
  };

  const logout = async () => {
    // Clear JWT auth
    localStorage.removeItem("user");
    setJwtSession(null);
    setIsAdmin(false);
  };

  // Check if token has expired
  const checkTokenExpiration = () => {
    if (!isAuthenticated || !jwtSession) return;

    try {
      const token = jwtSession.user.jwt;
      if (!token) return;

      const decoded = jwtDecode(token);
      const expirationTime = decoded.exp as number;
      const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds

      if (expirationTime && expirationTime < currentTime) {
        // Token has expired, logout the user
        console.log('JWT token expired, logging out user');
        logout();
        navigate('/login', { replace: true });
        return true;
      }
      return false;
    } catch (err) {
      console.error("Error checking token expiration:", err);
      return false;
    }
  };

  // Return the user session
  const activeUser = jwtSession?.user || null;

  return {
    session: jwtSession,
    user: activeUser,
    isAuthenticated,
    isLoading,
    isAdmin,
    redirectToLogin,
    logout,
    checkTokenExpiration
  };
};
